import { useEffect, useMemo, useState } from 'react';
import { Field } from '@/isomorphic/components/forms/components/field';
import { AccountFieldProps } from '../types';

const sanitize = (value: string) => {
    if (!value) {
        return '';
    }

    return value.replace(/[^\d.\s-]/g, '');
};

export const validateClearing = (value: string | number) => {
    if (!value) {
        return true;
    }

    return /^[0-9]{4,5}$/.test(`${value}`.replace(/[^\d]+/g, '')) === true;
};

export const validateAccount = (value: string | number) => {
    if (!value) {
        return true;
    }

    return /^[0-9]{6,12}$/.test(`${value}`.replace(/[^\d]+/g, '')) === true;
};

export function AccountFields({ values, setValues, onValidChange, getLabel, showValidationErrors = false }: AccountFieldProps) {
    const [clearingTouched, setClearingTouched] = useState(false);
    const [accountTouched, setAccountTouched] = useState(false);

    const clearing = useMemo(() => values.clearingNumber ?? '', [values.clearingNumber]);
    const account = useMemo(() => values.accountNumber ?? '', [values.accountNumber]);

    const onlyOneFilled = useMemo(() => (!!clearing && !account) || (!clearing && !!account), [clearing, account]);
    const clearingValidated = useMemo(() => validateClearing(clearing) === true, [clearing]);
    const accountValidated = useMemo(() => validateAccount(account) === true, [account]);
    const isValid = useMemo(() => !onlyOneFilled && clearingValidated && accountValidated, [onlyOneFilled, clearingValidated, accountValidated]);

    const onlyOneFilledMessage = useMemo(
        () =>
            onlyOneFilled && clearingTouched && accountTouched
                ? !clearing
                    ? getLabel('missingClearingWhenAccount')
                    : getLabel('missingAccountWhenClearing')
                : '',
        [clearingTouched, accountTouched]
    );

    useEffect(() => {
        onValidChange?.(isValid);
    }, [isValid, onValidChange]);

    return (
        <div>
            <Field
                type="text"
                grouped="top"
                value={clearing}
                validation={() => clearingValidated}
                onBlur={() => setClearingTouched(true)}
                label={`${getLabel('clearingNumber')} *`}
                infoText={getLabel('clearingNumberInfo')}
                onChange={v => setValues({ clearingNumber: sanitize(v) })}
                errorMessage={!clearingValidated ? getLabel('clearingTooShort') : ''}
                showRequiredError={showValidationErrors && (!clearing || !clearingValidated)}
            />
            <Field
                type="text"
                grouped="bottom"
                value={account}
                validation={() => accountValidated}
                onBlur={() => setAccountTouched(true)}
                label={`${getLabel('accountNumber')} *`}
                infoText={getLabel('accountNumberInfo')}
                onChange={v => setValues({ accountNumber: sanitize(v) })}
                errorMessage={!accountValidated ? getLabel('accountOutOfRange') : ''}
                showRequiredError={showValidationErrors && (!account || !accountValidated)}
            />

            {onlyOneFilledMessage && <div className="r-my-2 r-rounded-xl r-bg-warning r-p-4 r-text-sm r-text-white">{onlyOneFilledMessage}</div>}
        </div>
    );
}
